import { memo } from 'react';
import { EditorStarterAsset } from '../../assets/assets';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import {
  FEATURE_AUDIO_WAVEFORM_FOR_VIDEO_ITEM,
  FEATURE_FILMSTRIP,
  FEATURE_FILMSTRIP_V2,
  FEATURE_TIMELINE_VOLUME_CONTROL,
  FEATURE_VIDEO_WAVEFORM,
  FEATURE_VISUAL_FADE_CONTROL,
  FEATURE_WAVEFORM,
} from '../../flags';
import { AudioItem } from '../../items/audio/audio-item-type';
import { EditorStarterItem } from '../../items/item-type';
import {
  hasEmbeddedLipsyncAudio,
  VideoItem,
} from '../../items/video/video-item-type';
import { TRACK_PADDING } from '../../state/items';
import { canApplyAnimation } from '../../state/actions/add-animation';
import { getCanFadeVisual } from '../../utils/fade';
import { shouldShowItemHeader } from '../../utils/position-utils';
import { useAssetIfApplicable, useFps } from '../../utils/use-context';
import { useTimelineSize } from '../utils/use-timeline-size';
import { FadeCurve } from './timeline-item-fade-control/fade-curve';
import {
  FILMSTRIP_HEIGHT_IF_THERE_IS_AUDIO,
  TimelineItemFilmStrip,
} from './timeline-item-film-strip';
import {
  FILMSTRIP_HEIGHT_IF_THERE_IS_AUDIO_V2,
  TimelineItemFilmStripV2,
} from './timeline-item-film-strip-v2';
import { TimelineItemHeader } from './timeline-item-header';
import { TIMELINE_ITEM_BORDER_WIDTH } from './timeline-item-layout';
import { TimelineItemPreview } from './timeline-item-preview';
import { TimelineItemVolumeControl } from './timeline-item-volume-control/timeline-item-volume-control';
import { TimelineItemWaveform } from './timeline-item-waveform';
import { TimelineItemAnimationIndicator } from './timeline-item-animation-indicator';
import { TimelineItemKeyframeMarkers } from './timeline-item-keyframe-markers';

const shouldShowAudioWaveform = ({
  item,
  asset,
}: {
  item: EditorStarterItem;
  asset: EditorStarterAsset | null;
}) => {
  if (!FEATURE_WAVEFORM) {
    return false;
  }

  if (item.type === 'audio') {
    return true;
  }

  if (item.type === 'video') {
    const shouldShowEmbeddedAudio = hasEmbeddedLipsyncAudio(item);
    if (!FEATURE_VIDEO_WAVEFORM && !shouldShowEmbeddedAudio) {
      return false;
    }

    if (!asset || asset.type !== 'video') {
      throw new Error('Expected video asset in shouldShowAudioWaveform');
    }

    return (
      asset.hasAudioTrack &&
      (FEATURE_AUDIO_WAVEFORM_FOR_VIDEO_ITEM || shouldShowEmbeddedAudio)
    );
  }

  // audio waveform이 없는 모든 item type들을 여기에 추가
  if (
    item.type === 'captions' ||
    item.type === 'gif' ||
    item.type === 'webp' ||
    item.type === 'text' ||
    item.type === 'solid' ||
    item.type === 'blur' ||
    item.type === 'image' ||
    item.type === 'pdf' ||
    item.type === 'ppt' ||
    item.type === 'code' ||
    item.type === 'group'
  ) {
    return false;
  }

  throw new Error('Invalid item type: ' + (item satisfies never));
};

export const TimelineItemContent = memo(
  ({
    item,
    height,
    width,
    roundedDifference,
    trackMuted,
  }: {
    item: EditorStarterItem;
    height: number;
    width: number;
    roundedDifference: number;
    trackMuted: boolean;
  }) => {
    const { fps } = useFps();
    const asset = useAssetIfApplicable(item);
    const { timelineWidth } = useTimelineSize();

    if (timelineWidth === null) {
      throw new Error('Timeline width is null');
    }

    // V2 사용 시 높이 계산 분기
    const filmstripHeight = FEATURE_FILMSTRIP_V2
      ? asset?.type === 'video' && !asset.hasAudioTrack
        ? height
        : FILMSTRIP_HEIGHT_IF_THERE_IS_AUDIO_V2
      : asset?.type === 'video' && !asset.hasAudioTrack
        ? height
        : FILMSTRIP_HEIGHT_IF_THERE_IS_AUDIO;

    return (
      <>
        <div className="absolute h-full w-full">
          <TimelineItemPreview
            item={item}
            asset={asset}
          />
          {/* FilmStrip V2: 에셋 단위 프레임 추출로 성능 개선 */}
          {item.type === 'video' && FEATURE_FILMSTRIP_V2 && asset && (
            <RequireCachedAsset asset={asset}>
              <TimelineItemFilmStripV2
                item={item}
                startFrom={item.videoStartFromInSeconds * fps}
                durationInFrames={item.durationInFrames}
                fps={fps}
                roundedDifference={roundedDifference}
                height={filmstripHeight}
                playbackRate={item.playbackRate}
              />
            </RequireCachedAsset>
          )}
          {/* FilmStrip V1 (기존): V2가 꺼져있을 때만 사용 */}
          {item.type === 'video' &&
            FEATURE_FILMSTRIP &&
            !FEATURE_FILMSTRIP_V2 &&
            asset && (
              <RequireCachedAsset asset={asset}>
                <TimelineItemFilmStrip
                  item={item}
                  startFrom={item.videoStartFromInSeconds * fps}
                  durationInFrames={item.durationInFrames}
                  fps={fps}
                  roundedDifference={roundedDifference}
                  height={filmstripHeight}
                  playbackRate={item.playbackRate}
                />
              </RequireCachedAsset>
            )}
          {getCanFadeVisual(item) && FEATURE_VISUAL_FADE_CONTROL && (
            <FadeCurve
              item={item}
              height={
                item.type === 'video'
                  ? filmstripHeight
                  : height - TRACK_PADDING - TIMELINE_ITEM_BORDER_WIDTH * 2
              }
              width={width}
              fadeType="visual"
            />
          )}
          {asset && shouldShowAudioWaveform({ item, asset }) ? (
            <RequireCachedAsset asset={asset}>
              <TimelineItemWaveform
                trackHeight={height}
                item={item as AudioItem | VideoItem}
                timelineWidth={timelineWidth}
                roundedDifference={roundedDifference}
                trackMuted={trackMuted}
              >
                {FEATURE_TIMELINE_VOLUME_CONTROL ? (
                  <TimelineItemVolumeControl
                    item={item as AudioItem | VideoItem}
                  />
                ) : null}
              </TimelineItemWaveform>
            </RequireCachedAsset>
          ) : null}
          {shouldShowItemHeader(item) && asset && asset.filename ? (
            <TimelineItemHeader filename={asset.filename} />
          ) : null}
          {/* 실제 렌더러가 적용하는 Animation만 표시 */}
          {canApplyAnimation(item) &&
            'animations' in item &&
            Array.isArray(item.animations) &&
            item.animations.length > 0 && (
              <TimelineItemAnimationIndicator
                animationTypes={item.animations.map((anim) => anim.type)}
              />
            )}
          {/* 위치 키프레임 마커 — 선택된 아이템에만 표시(내부에서 선택·키프레임 여부 판단) */}
          <TimelineItemKeyframeMarkers item={item} />
        </div>
      </>
    );
  },
);

TimelineItemContent.displayName = 'TimelineItemContent';
